import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, CheckCircle2, AlertCircle, Star, Calendar,
  ArrowLeft,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_STORAGE_PREFIX = "survey_rt:";

/**
 * Public anonymous survey page — one question per slide, autosave on every
 * answer, no submit button. `respondent_token` is persisted in localStorage
 * keyed by the survey's public token so a refresh resumes where the user
 * left off. The Thank-You slide is shown after the last question.
 *
 * No auth header is sent — these endpoints are intentionally unauthenticated.
 */
export default function SurveyTake({ invitationMode = false }) {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState({});
  const [respondentToken, setRespondentToken] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState("forward");
  const [loadError, setLoadError] = useState(null);
  const [thankYou, setThankYou] = useState(false);
  // In invitation mode we accumulate answers locally and submit once at the
  // end (one POST to /i/{token}/submit) instead of per-question autosave.
  const localAnswersRef = useRef({});

  // Load survey on mount. Per user spec: EVERY click on a survey link
  // must land the recipient on the first question — we deliberately do
  // NOT auto-jump to a "you've already completed it" screen, and we
  // start from step 0 with empty answers each visit.
  useEffect(() => {
    const storageKey = TOKEN_STORAGE_PREFIX + (invitationMode ? "i:" : "") + token;
    (async () => {
      try {
        if (invitationMode) {
          // Per-recipient invitation path: backend resolves the token →
          // survey + pre-filled email. Marks `opened_at` on the
          // invitation row server-side.
          const { data } = await axios.get(`${API}/public/surveys/i/${token}`);
          setSurvey(data.survey);
          setInvitation(data.invitation);
          // Fresh attempt every visit — don't pre-fill prior answers.
          setSavedAnswers({});
          localAnswersRef.current = {};
          setStep(0);
          return;
        }
        // Anonymous public link: always create a brand-new response so
        // every visitor sees the question page (no shared "completed"
        // bleed-through). We DON'T reuse the localStorage token.
        const { data } = await axios.get(`${API}/public/surveys/${token}`);
        setSurvey(data.survey);
        setSavedAnswers({});
        setStep(0);
        const { data: s } = await axios.post(`${API}/public/surveys/${token}/start`, {});
        localStorage.setItem(storageKey, s.respondent_token);
        setRespondentToken(s.respondent_token);
      } catch (e) {
        setLoadError(e.response?.data?.detail || "This survey is not available.");
      }
    })();
  }, [token, invitationMode]);

  const total = survey?.questions?.length || 0;
  const currentQ = survey?.questions?.[step];

  const goNext = () => {
    setDirection("forward");
    if (step + 1 >= total) {
      if (invitationMode) {
        // Single-shot submit: send everything accumulated so far in one POST.
        axios.post(`${API}/public/surveys/i/${token}/submit`, {
          answers: localAnswersRef.current,
        }).catch(() => {});
      } else if (respondentToken) {
        axios.post(`${API}/public/surveys/${token}/complete`, {
          respondent_token: respondentToken,
        }).catch(() => {});
      }
      setThankYou(true);
    } else {
      setStep((s) => s + 1);
    }
  };

  const goPrev = () => {
    if (step === 0) return;
    setDirection("backward");
    setStep((s) => s - 1);
  };

  if (loadError) {
    return (
      <PageShell>
        <div className="text-center py-12">
          <AlertCircle className="mx-auto text-amber-500" size={42} strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-bold mt-4">Survey unavailable</h1>
          <p className="text-muted-foreground mt-2 text-sm">{loadError}</p>
        </div>
      </PageShell>
    );
  }

  if (!survey) {
    return (
      <PageShell>
        <div className="text-center py-12 text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading survey…
        </div>
      </PageShell>
    );
  }

  if (thankYou) {
    return (
      <PageShell>
        <div data-testid="survey-thank-you" className="text-center py-16">
          <div className="inline-block">
            <CheckCircle2 className="mx-auto text-primary" size={56} strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-4xl font-bold mt-6">Thank you!</h1>
          <p className="text-muted-foreground mt-3 text-sm max-w-md mx-auto">
            Your responses have been recorded. Feel free to close this tab.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mb-6">
        <div className="meta-label">{survey.campaign_name || ""}</div>
        <h1 className="font-display text-2xl font-bold mt-1">{survey.title}</h1>
        {survey.description && (
          <p className="text-sm text-muted-foreground mt-2">{survey.description}</p>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-secondary rounded-full overflow-hidden mb-8" data-testid="survey-progress-bar">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((step) / total) * 100}%` }}
        />
      </div>

      <div className="text-xs text-muted-foreground mb-2" data-testid="survey-question-counter">
        Question {step + 1} of {total}
      </div>

      <SlideStage key={step} direction={direction}>
        <QuestionSlide
          question={currentQ}
          initialValue={savedAnswers[currentQ?.id]}
          token={token}
          respondentToken={respondentToken}
          invitationMode={invitationMode}
          localStash={localAnswersRef}
          onSaved={(qid, val) => setSavedAnswers((m) => ({ ...m, [qid]: val }))}
          onNext={goNext}
        />
      </SlideStage>
      {invitation?.email && (
        <div className="mt-3 text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="survey-invitation-banner">
          Responding as <span className="font-mono">{invitation.email}</span>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={goPrev}
          disabled={step === 0}
          data-testid="survey-prev"
          className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
        >
          <ArrowLeft size={11} /> Back
        </button>
        <div className="text-[10px] text-muted-foreground">
          Each answer auto-saves
        </div>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-background flex items-start justify-center p-6 pt-16">
      <div className="w-full max-w-2xl">{children}</div>
    </div>
  );
}

function SlideStage({ direction, children }) {
  // Pure-CSS slide-in animation keyed by `step` (provided by parent via key prop).
  const cls = direction === "forward" ? "animate-slide-in-right" : "animate-slide-in-left";
  return <div className={cls}>{children}</div>;
}

function QuestionSlide({ question, initialValue, token, respondentToken, invitationMode, localStash, onSaved, onNext }) {
  const [value, setValue] = useState(initialValue ?? defaultFor(question));
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const debounceRef = useRef(null);
  const autoAdvanceRef = useRef(null);
  const valRef = useRef(value);
  valRef.current = value;
  const advancedRef = useRef(false);

  // Clear any pending timers on unmount (when slide changes).
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(autoAdvanceRef.current);
    };
  }, []);

  const persist = async (v) => {
    if (!question) return false;
    // Invitation mode: stash locally, single POST at the end (no per-q API).
    if (invitationMode) {
      if (localStash?.current) localStash.current[question.id] = v;
      setSaveState("saved");
      onSaved?.(question.id, v);
      return true;
    }
    if (!respondentToken) return false;
    setSaveState("saving");
    try {
      await axios.put(`${API}/public/surveys/${token}/answer`, {
        respondent_token: respondentToken,
        question_id: question.id,
        value: v,
      });
      setSaveState("saved");
      onSaved?.(question.id, v);
      return true;
    } catch {
      setSaveState("error");
      return false;
    }
  };

  // Fires the auto-advance after a short delay. Guarded so multiple
  // selections on the same slide can't double-advance.
  const scheduleAdvance = (delayMs) => {
    clearTimeout(autoAdvanceRef.current);
    autoAdvanceRef.current = setTimeout(() => {
      if (advancedRef.current) return;
      if (!hasValue(valRef.current)) return; // required-guard
      advancedRef.current = true;
      onNext();
    }, delayMs);
  };

  // Debounced persist for typed inputs. We DON'T auto-advance on typing —
  // user might pause to think. They press Enter to advance, or for date pick
  // we advance on the change event.
  const debouncePersist = (v) => {
    setValue(v);
    clearTimeout(debounceRef.current);
    clearTimeout(autoAdvanceRef.current); // cancel any pending auto-advance while typing
    setSaveState("saving");
    debounceRef.current = setTimeout(() => persist(v), 450);
  };

  // For click-based inputs: persist immediately then schedule advance.
  // Per user spec — keep a proper pulse so the recipient SEES their pick
  // highlight before the slide transitions:
  // - single_choice / yes_no / rating: 700ms after click
  // - multi_choice: 1800ms after LAST toggle (user can still add more picks)
  // - date: 500ms after selection
  const instantPersist = async (v) => {
    setValue(v);
    clearTimeout(debounceRef.current);
    const ok = await persist(v);
    if (!ok) return;
    let delay;
    if (question.type === "multi_choice") delay = 1800;
    else if (question.type === "date") delay = 500;
    else delay = 700;
    scheduleAdvance(delay);
  };

  // Enter-key handler for short_text / long_text / number.
  // (long_text uses Shift+Enter for newline, plain Enter advances.)
  const handleKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    if (question.type === "long_text" && e.shiftKey) return; // newline
    e.preventDefault();
    if (!hasValue(valRef.current)) return;
    // Flush any pending debounced save first.
    clearTimeout(debounceRef.current);
    const ok = await persist(valRef.current);
    if (ok && !advancedRef.current) {
      advancedRef.current = true;
      onNext();
    }
  };

  if (!question) return null;

  const hint = hintForType(question.type);

  return (
    <div data-testid={`survey-question-${question.id}`}>
      <h2 className="font-display text-2xl md:text-3xl font-bold leading-snug">
        {question.prompt}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </h2>

      <div className="mt-8" data-testid="survey-question-input">
        <QuestionInput
          question={question}
          value={value}
          onTextChange={debouncePersist}
          onChoiceChange={instantPersist}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="mt-6 flex items-center gap-3 text-[11px] text-muted-foreground">
        <span data-testid="survey-hint">{hint}</span>
        <SaveIndicator state={saveState} />
      </div>
    </div>
  );
}

function hintForType(type) {
  switch (type) {
    case "short_text":
    case "number":
      return "Press Enter ↵ to continue";
    case "long_text":
      return "Press Enter ↵ to continue (Shift + Enter for new line)";
    case "date":
    case "yes_no":
    case "rating":
    case "single_choice":
      return "Pick an option to continue";
    case "multi_choice":
      return "Tap as many as apply — we'll move on automatically";
    default:
      return "";
  }
}

function SaveIndicator({ state }) {
  if (state === "saving") return <span data-testid="save-indicator" className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" />Saving…</span>;
  if (state === "saved") return <span data-testid="save-indicator" className="text-[11px] text-primary inline-flex items-center gap-1"><CheckCircle2 size={10} />Saved</span>;
  if (state === "error") return <span data-testid="save-indicator" className="text-[11px] text-red-600 inline-flex items-center gap-1"><AlertCircle size={10} />Save failed</span>;
  return null;
}

function defaultFor(q) {
  if (!q) return "";
  if (q.type === "multi_choice") return [];
  if (q.type === "yes_no") return null;
  if (q.type === "rating") return null;
  if (q.type === "number") return "";
  return "";
}

function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
}

function QuestionInput({ question, value, onTextChange, onChoiceChange, onKeyDown }) {
  switch (question.type) {
    case "short_text":
      return (
        <input
          type="text"
          autoFocus
          value={value || ""}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type your answer…"
          className="form-input w-full text-lg"
          data-testid="input-short-text"
        />
      );
    case "long_text":
      return (
        <textarea
          autoFocus
          rows={4}
          value={value || ""}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type your answer…"
          className="form-input w-full text-base"
          data-testid="input-long-text"
        />
      );
    case "number":
      return (
        <input
          type="number"
          autoFocus
          value={value || ""}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="0"
          className="form-input w-full text-lg"
          data-testid="input-number"
        />
      );
    case "date":
      return (
        <div className="relative">
          <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="date"
            value={value || ""}
            // Date pickers fire one change event when the user commits a value
            // (mouse click or arrow-key + Enter inside the native picker) so
            // we treat this as a "click-equivalent" and auto-advance.
            onChange={(e) => e.target.value && onChoiceChange(e.target.value)}
            className="form-input w-full pl-9 text-base"
            data-testid="input-date"
          />
        </div>
      );
    case "yes_no":
      return (
        <div className="flex gap-3">
          {["Yes", "No"].map((opt) => (
            <button
              key={opt}
              onClick={() => onChoiceChange(opt)}
              data-testid={`input-yes-no-${opt.toLowerCase()}`}
              className={`flex-1 border rounded-sm px-4 py-3 text-base font-medium transition-colors ${
                value === opt
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:bg-secondary"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    case "single_choice":
      return (
        <div className="space-y-2">
          {(question.options || []).map((opt) => (
            <button
              key={opt}
              onClick={() => onChoiceChange(opt)}
              data-testid={`input-single-${opt}`}
              className={`w-full text-left border rounded-sm px-4 py-3 text-base flex items-center gap-3 transition-colors ${
                value === opt
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:bg-secondary"
              }`}
            >
              <span className={`w-4 h-4 rounded-full border-2 ${value === opt ? "border-primary bg-primary" : "border-muted-foreground"}`} />
              {opt}
            </button>
          ))}
        </div>
      );
    case "multi_choice": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) => {
        const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
        onChoiceChange(next);
      };
      return (
        <div className="space-y-2">
          {(question.options || []).map((opt) => (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              data-testid={`input-multi-${opt}`}
              className={`w-full text-left border rounded-sm px-4 py-3 text-base flex items-center gap-3 transition-colors ${
                arr.includes(opt)
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:bg-secondary"
              }`}
            >
              <span className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${arr.includes(opt) ? "border-primary bg-primary" : "border-muted-foreground"}`}>
                {arr.includes(opt) && <span className="text-primary-foreground text-[10px] leading-none">✓</span>}
              </span>
              {opt}
            </button>
          ))}
        </div>
      );
    }
    case "rating": {
      const min = question.min_value ?? 1;
      const max = question.max_value ?? 5;
      const values = [];
      for (let i = min; i <= max; i++) values.push(i);
      return (
        <div className="flex gap-2 flex-wrap">
          {values.map((n) => (
            <button
              key={n}
              onClick={() => onChoiceChange(n)}
              data-testid={`input-rating-${n}`}
              className={`flex flex-col items-center gap-1 border rounded-sm px-4 py-3 transition-colors ${
                value === n ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"
              }`}
            >
              <Star size={18} fill={value !== null && value >= n ? "currentColor" : "none"} />
              <span className="text-[10px] font-mono">{n}</span>
            </button>
          ))}
        </div>
      );
    }
    default:
      return <div className="text-xs text-muted-foreground">Unsupported question type</div>;
  }
}
